<template>
  <div id="rovarci_layout">
    <div
      id="rovarci_container"
      class="rovarci-container"
    >
      <div class="verona-contact-form">
        <ContactForm>
          <div
            slot="title"
            class="group-title"
          >
            <h2 class="title">
              {{ $t('Need Help?') }}
            </h2>
            <br>
            <h4>
              {{ $t('Fill out the form below and we will reach back to you!') }}
            </h4>
          </div>
        </ContactForm>
      </div>
      <StoryHeading
        v-if="storeHeadingRef && storeHeadingRef.data"
        :heading="storeHeadingRef.data.heading"
        :content="storeHeadingRef.data.content"
      />
      <div
        v-if="storesRef && storesRef.stores"
        class="verona-container verona-list"
      >
        <div
          v-for="(store, index) in storesRef.stores"
          :key="index"
        >
          <div
            :id="store.hash"
            class="hidden"
          >
            {{ store.title }}
          </div>
          <StoreItem :store="store" />
        </div>
      </div>
    </div>

    <div class="rovarci_gallery">
      <StoryGallery />
    </div>
    <PageGtm />
    <PageTrackingGtm
      v-if="metaTitle"
      :page-title-gtm="metaTitle"
    />
  </div>
</template>

<script>
import {
  computed, defineComponent, onMounted, ref,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@gemini/vsf-cache';
import { onSSR } from '@vue-storefront/core';
import { contentGetters, useContent } from '@vue-storefront/gemini';
import StoryHeading from '~/components/StoryHeading.vue';
import StoryGallery from '~/components/StoryGallery.vue';
import StoreItem from '~/components/StoreItem.vue';
import ContactForm from '~/components/ContactForm.vue';
import { useI18n } from '~/helpers/hooks/usei18n';
import PageGtm from '~/components/Gtm/PageGtm.vue';
import PageTrackingGtm from '~/components/Gtm/PageTrackingGtm.vue';
import { useKlaviyo } from '~/composables';

export default defineComponent({
  name: 'ContactUsEe',
  components: {
    StoryHeading,
    StoryGallery,
    StoreItem,
    ContactForm,
    PageGtm,
    PageTrackingGtm,
  },
  setup() {
    const { addTags } = useCache();
    onSSR(() => {
      addTags([{ prefix: CacheTagPrefix.View, value: 'stores' }]);
    });
    const { locale } = useI18n();
    const { blocks } = useContent(`cmsBlockFooter-${locale}`);
    const { klaviyoPagePush } = useKlaviyo();

    const identifierContact = `storeHeading-${locale}`;
    const identifierContactStore = `store-${locale}`;

    const storeHeadingRef = ref(false);
    const storesRef = ref(false);
    const storeHeading = computed(() => contentGetters.getCmsBlockContent(blocks.value)).value[identifierContact];
    const stores = computed(() => contentGetters.getCmsBlockContent(blocks.value)).value[identifierContactStore];

    const metaTitle = locale === 'en' ? 'Need help?' : 'Hai bisogno di assistenza?';
    const metaDescription = locale === 'en' ? 'You can contact us by email (info@lazzarionline.com) or call us at (+39) 045 7614582' : 'Se ti serve aiuto puoi contattarci tramite mail (info@lazzarionline.com) oppure puoi chiamarci al numero +39 045 7614582.';

    onMounted(() => {
      klaviyoPagePush('Contact us');
    });

    return {
      stores,
      storeHeading,
      storesRef,
      storeHeadingRef,
      identifierContact,
      identifierContactStore,
      metaTitle,
      metaDescription,
    };
  },
  head() {
    return {
      title: this?.metaTitle,
      meta: [
        { hid: 'robots', name: 'robots', content: 'index, follow' },
        { hid: 'og:description', property: 'og:description', content: this?.metaDescription },
        { hid: 'description', name: 'description', content: this?.metaDescription },
        { hid: 'og:title', property: 'og:title', content: this?.metaTitle },
      ],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.storeHeadingRef = this.storeHeading;
      this.storesRef = this.stores;
    });
  },
  updated() {
    if (
      this.$route
      && this.$route.hash
      && document.querySelector(this.$route.hash)
    ) {
      const scrollDiv = document.querySelector(this.$route.hash).offsetTop;
      if (scrollDiv) {
        window.scrollTo({ top: scrollDiv - 60, behavior: 'smooth' });
      }
    }
  },
});
</script>

<style lang="scss" scoped>
.verona-container {
  margin: 0 auto;
  padding: 0 24px;
  max-width: 1414px;
}

.hidden {
  visibility: hidden;
}

.rovarci-container {
  background-color: var(--_c-pink-primary);
}

.verona-contact-form {
  margin: 0 auto;
  max-width: 588px;
  padding-bottom: 75px;
  padding-top: 75px;
  .title {
    font-family: 'Frank Ruhl Libre';
    font-size: 52px;
    font-weight: 400;
    line-height: 52px;
    margin-top: 10px;
    color: #222a24;
    text-align: center;
    &:first-child {
      margin-top: 0;
    }
  }

  .group-title {
    margin-bottom: 2px;
  }
}

.rovarci_gallery {
  max-width: 1240px;
  padding: 0 24px;
  margin: 0 auto;
}

.verona-list {
  & > div {
    margin-bottom: 45px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

@media screen and (max-width: 768.98px) {
  .verona-contact-form {
    padding-bottom: 34px;
    padding-top: 41px;
    .title {
      font-size: 28px;
      line-height: 28px;
      margin-top: 10px;
    }

    .group-title {
      margin-bottom: 7px;
    }
  }
  .verona-list {
    & > div {
      margin-bottom: 24px;
    }
  }
}
</style>
